import { useState, useEffect } from "react";
import { useParams } from "@/lib/router";
import {
  Copy,
  Share2,
  QrCode,
  Send,
  ArrowDownLeft,
  Clock,
  Wallet,
  Shield,
  Check,
  Loader2,
  TrendingUp,
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";

const ETH_ADDRESS_REGEX = /^0x[0-9a-fA-F]{40}$/;

const RESERVED_PATHS = new Set([
  "agents", "trade", "home", "login", "settings", "api", "overview",
  "tasks", "chat", "sessions", "logs", "cron", "memory", "users",
  "history", "activity", "audit", "webhooks", "alerts", "gateways",
  "integrations", "super-admin", "office", "monitor", "skills", "channels",
  "nodes", "security", "poi", "debug", "namedrop", "block", "collections",
  "setup", "standalone", "widgets", "docs", "p", "u", "profile",
]);

function isValidAddress(address: string): boolean {
  if (!address) return false;
  if (RESERVED_PATHS.has(address.toLowerCase())) return false;
  return ETH_ADDRESS_REGEX.test(address);
}

interface TokenBalance {
  symbol: string;
  name: string;
  balance: string;
  price: number;
  change24h: number;
}

const MOCK_BALANCES: TokenBalance[] = [
  { symbol: "OTC", name: "OTC Token", balance: "12,450.00", price: 1.0, change24h: 2.5 },
  { symbol: "ETH", name: "Ethereum", balance: "2.50", price: 3200.0, change24h: -1.2 },
  { symbol: "USDO", name: "USD Omni", balance: "5,000.00", price: 1.0, change24h: 0.0 },
];

const MOCK_TRANSACTIONS = [
  { id: "1", type: "receive" as const, from: "0x1234...5678", amount: "+1,000 OTC", time: "2 min ago", status: "completed" as const },
  { id: "2", type: "send" as const, to: "0xabcd...efgh", amount: "-500 OTC", time: "1 hour ago", status: "completed" as const },
  { id: "3", type: "receive" as const, from: "0x9876...5432", amount: "+2.5 ETH", time: "3 hours ago", status: "completed" as const },
];

export default function AddressProfilePage() {
  const params = useParams<{ address: string }>();
  const address = params.address || "";
  const { toast } = useToast();

  const [copied, setCopied] = useState(false);
  const [activeTab, setActiveTab] = useState<"tokens" | "activity">("tokens");
  const [isDelegating, setIsDelegating] = useState(false);
  const [delegated, setDelegated] = useState(false);

  useEffect(() => {
    if (copied) {
      const t = setTimeout(() => setCopied(false), 2000);
      return () => clearTimeout(t);
    }
  }, [copied]);

  if (!isValidAddress(address)) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50 dark:bg-gray-900">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Invalid Address</h1>
          <p className="text-gray-500 dark:text-gray-400">The address format is not valid.</p>
        </div>
      </div>
    );
  }

  const handleCopy = () => {
    navigator.clipboard.writeText(address);
    setCopied(true);
    toast({ title: "Address copied to clipboard" });
  };

  const handleDelegate = () => {
    setIsDelegating(true);
    setTimeout(() => {
      setIsDelegating(false);
      setDelegated(true);
      toast({ title: "Smart wallet activated", description: "ERC-7702 delegation created (mock)" });
    }, 1500);
  };

  const shortAddress = `${address.slice(0, 6)}...${address.slice(-4)}`;

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-3xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center text-white text-xl font-bold">
                {address.slice(2, 4).toUpperCase()}
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-900 dark:text-white">{shortAddress}</h1>
                <p className="text-sm text-gray-500 dark:text-gray-400 font-mono">{address}</p>
              </div>
            </div>
            <div className="flex gap-2">
              <button
                onClick={handleCopy}
                className="p-2 rounded-lg bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                title="Copy address"
              >
                {copied ? <Check className="w-5 h-5 text-green-500" /> : <Copy className="w-5 h-5 text-gray-600 dark:text-gray-300" />}
              </button>
              <button className="p-2 rounded-lg bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors" title="Share">
                <Share2 className="w-5 h-5 text-gray-600 dark:text-gray-300" />
              </button>
              <button className="p-2 rounded-lg bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors" title="QR Code">
                <QrCode className="w-5 h-5 text-gray-600 dark:text-gray-300" />
              </button>
            </div>
          </div>

          {/* Balance Card */}
          <div className="mt-6 p-4 rounded-xl bg-gradient-to-r from-blue-600 to-purple-600 text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm opacity-80">Total Balance</p>
                <p className="text-3xl font-bold">$24,892.50</p>
                <p className="text-sm opacity-80 mt-1 flex items-center gap-1">
                  <TrendingUp className="w-4 h-4" /> +2.4% today
                </p>
              </div>
              <Wallet className="w-12 h-12 opacity-30" />
            </div>
          </div>

          {/* Actions */}
          <div className="mt-4 flex gap-3">
            <button className="flex-1 py-3 rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-medium flex items-center justify-center gap-2 transition-colors">
              <Send className="w-4 h-4" /> Send
            </button>
            <button className="flex-1 py-3 rounded-lg bg-green-600 hover:bg-green-700 text-white font-medium flex items-center justify-center gap-2 transition-colors">
              <ArrowDownLeft className="w-4 h-4" /> Receive
            </button>
            {!delegated ? (
              <button
                onClick={handleDelegate}
                disabled={isDelegating}
                className="flex-1 py-3 rounded-lg bg-purple-600 hover:bg-purple-700 text-white font-medium flex items-center justify-center gap-2 transition-colors disabled:opacity-50"
              >
                {isDelegating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Shield className="w-4 h-4" />}
                {isDelegating ? "Activating..." : "Delegate"}
              </button>
            ) : (
              <button className="flex-1 py-3 rounded-lg bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300 font-medium flex items-center justify-center gap-2 cursor-default">
                <Check className="w-4 h-4 text-green-500" /> Delegated
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="max-w-3xl mx-auto px-4 mt-6">
        <div className="flex gap-4 border-b border-gray-200 dark:border-gray-700">
          <button
            onClick={() => setActiveTab("tokens")}
            className={`pb-3 text-sm font-medium transition-colors ${
              activeTab === "tokens"
                ? "text-blue-600 dark:text-blue-400 border-b-2 border-blue-600"
                : "text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
            }`}
          >
            Tokens
          </button>
          <button
            onClick={() => setActiveTab("activity")}
            className={`pb-3 text-sm font-medium transition-colors ${
              activeTab === "activity"
                ? "text-blue-600 dark:text-blue-400 border-b-2 border-blue-600"
                : "text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
            }`}
          >
            Activity
          </button>
        </div>

        {/* Tokens Tab */}
        {activeTab === "tokens" && (
          <div className="mt-4 space-y-3">
            {MOCK_BALANCES.map((token) => (
              <div
                key={token.symbol}
                className="p-4 rounded-xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 flex items-center justify-between"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center text-sm font-bold text-gray-700 dark:text-gray-300">
                    {token.symbol[0]}
                  </div>
                  <div>
                    <p className="font-medium text-gray-900 dark:text-white">{token.name}</p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">{token.balance} {token.symbol}</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="font-medium text-gray-900 dark:text-white">${token.price.toLocaleString()}</p>
                  <p className={`text-sm ${token.change24h >= 0 ? "text-green-500" : "text-red-500"}`}>
                    {token.change24h >= 0 ? "+" : ""}{token.change24h}%
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Activity Tab */}
        {activeTab === "activity" && (
          <div className="mt-4 space-y-3">
            {MOCK_TRANSACTIONS.map((tx) => (
              <div
                key={tx.id}
                className="p-4 rounded-xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 flex items-center justify-between"
              >
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                    tx.type === "receive" ? "bg-green-100 dark:bg-green-900/30 text-green-600" : "bg-red-100 dark:bg-red-900/30 text-red-600"
                  }`}>
                    {tx.type === "receive" ? <ArrowDownLeft className="w-5 h-5" /> : <Send className="w-5 h-5" />}
                  </div>
                  <div>
                    <p className="font-medium text-gray-900 dark:text-white capitalize">{tx.type}</p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {tx.type === "receive" ? `From ${tx.from}` : `To ${tx.to}`}
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="font-medium text-gray-900 dark:text-white">{tx.amount}</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400 flex items-center justify-end gap-1">
                    <Clock className="w-3 h-3" /> {tx.time}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
